/**
 * Period Preset Selector Component
 *
 * Provides year buttons + monthly drill-down for the CEO dashboard.
 * - "Ano Atual" (YTD)
 * - Dynamic past-year buttons (2025, 2024, …) — grows as history accumulates
 * - "Mensal" toggle that shows month pills for the selected year
 *   → clicking months creates a contiguous range
 */

import { useCallback, useMemo, useState } from 'react';

// ---------------------------------------------------------------------------
// Types & helpers
// ---------------------------------------------------------------------------

export type PeriodPreset = 'ytd' | 'prevYear' | 'last12m' | 'last6m' | 'last3m' | 'custom';

export interface PeriodDates {
  startDate: string;
  endDate: string;
}

const OLDEST_YEAR = 2024;

const MONTH_LABELS = [
  'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
  'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez',
];

function fmt(d: Date): string {
  return d.toISOString().split('T')[0];
}

function lastDayOfMonth(year: number, month: number): Date {
  return new Date(year, month + 1, 0);
}

// ---------------------------------------------------------------------------
// Legacy helpers (kept for backward compat with other pages that use them)
// ---------------------------------------------------------------------------

export function calculatePeriodDates(preset: PeriodPreset): PeriodDates {
  const today = new Date();

  switch (preset) {
    case 'ytd': {
      return {
        startDate: fmt(new Date(today.getFullYear(), 0, 1)),
        endDate: fmt(today),
      };
    }
    case 'prevYear': {
      const prevYear = today.getFullYear() - 1;
      return {
        startDate: fmt(new Date(prevYear, 0, 1)),
        endDate: fmt(new Date(prevYear, 11, 31)),
      };
    }
    case 'last12m': {
      return {
        startDate: fmt(new Date(today.getFullYear(), today.getMonth() - 11, 1)),
        endDate: fmt(today),
      };
    }
    case 'last6m': {
      return {
        startDate: fmt(new Date(today.getFullYear(), today.getMonth() - 5, 1)),
        endDate: fmt(today),
      };
    }
    case 'last3m': {
      return {
        startDate: fmt(new Date(today.getFullYear(), today.getMonth() - 2, 1)),
        endDate: fmt(today),
      };
    }
    case 'custom':
    default:
      return { startDate: fmt(today), endDate: fmt(today) };
  }
}

export function detectPreset(startDate: string, endDate: string): PeriodPreset {
  const presets: PeriodPreset[] = ['ytd', 'prevYear', 'last12m', 'last6m', 'last3m'];
  for (const preset of presets) {
    const dates = calculatePeriodDates(preset);
    if (dates.startDate === startDate && dates.endDate === endDate) return preset;
  }
  return 'custom';
}

// ---------------------------------------------------------------------------
// Detect active state from current dates
// ---------------------------------------------------------------------------

interface ActiveState {
  type: 'ytd' | 'year' | 'months' | 'day' | 'custom';
  year: number;
  /** 0-based month indices when type === 'months' */
  monthStart?: number;
  monthEnd?: number;
  /** ISO date (YYYY-MM-DD) when type === 'day' */
  day?: string;
}

function detectActiveState(startDate: string, endDate: string): ActiveState {
  const today = new Date();
  const currentYear = today.getFullYear();

  // Parse dates
  const [sy, sm, sd] = startDate.split('-').map(Number);
  const [ey, em, ed] = endDate.split('-').map(Number);

  // Single day selection — startDate === endDate (but not a full year or clean month)
  if (startDate === endDate) {
    return { type: 'day', year: sy, day: startDate };
  }

  // Same year?
  if (sy === ey) {
    // Check if it's a full year (Jan 1 to Dec 31)
    if (sm === 1 && sd === 1 && em === 12 && ed === 31) {
      if (sy === currentYear) return { type: 'ytd', year: sy };
      return { type: 'year', year: sy };
    }

    // YTD: Jan 1 current year to today (not a clean month boundary)
    // Check this BEFORE month range so that Jan 1 → today isn't mistaken for a month selection
    if (sy === currentYear && sm === 1 && sd === 1) {
      const expectedEnd = lastDayOfMonth(ey, em - 1);
      const isLastDay = ed === expectedEnd.getDate();
      // If it ends exactly on last day of a month, it's a deliberate month selection, not YTD
      if (!isLastDay) {
        return { type: 'ytd', year: currentYear };
      }
    }

    // Check month range — starts on 1st, ends on last day of a month
    // For the current month of the current year, also accept today's date as valid end
    // (since we cap endDate at today to avoid distorting projections)
    if (sd === 1) {
      const expectedEnd = lastDayOfMonth(ey, em - 1);
      const isLastDay = ed === expectedEnd.getDate();
      const isCappedToday = ey === currentYear && em - 1 === today.getMonth() && ed === today.getDate();
      if (isLastDay || isCappedToday) {
        return { type: 'months', year: sy, monthStart: sm - 1, monthEnd: em - 1 };
      }
    }
  }

  return { type: 'custom', year: currentYear };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface PeriodPresetSelectorProps {
  startDate: string;
  endDate: string;
  onPeriodChange: (dates: PeriodDates) => void;
  /** Show as buttons (default) or dropdown */
  variant?: 'buttons' | 'dropdown';
}

export function PeriodPresetSelector({
  startDate,
  endDate,
  onPeriodChange,
  variant = 'buttons',
}: PeriodPresetSelectorProps) {
  const today = useMemo(() => new Date(), []);
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth(); // 0-based

  // Build list of past years dynamically
  const pastYears = useMemo(() => {
    const years: number[] = [];
    for (let y = currentYear - 1; y >= OLDEST_YEAR; y--) {
      years.push(y);
    }
    return years;
  }, [currentYear]);

  // Detect what's currently active
  const active = useMemo(() => detectActiveState(startDate, endDate), [startDate, endDate]);

  // Monthly mode state
  const [monthlyOpen, setMonthlyOpen] = useState(active.type === 'months');
  const [monthlyYear, setMonthlyYear] = useState(active.type === 'months' ? active.year : currentYear);

  // Daily mode state
  const [dailyOpen, setDailyOpen] = useState(active.type === 'day');

  // Button style helper
  const btnClass = (isActive: boolean) =>
    `px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-150 cursor-pointer
     focus:outline-none focus:ring-2 focus:ring-brand-primary
     ${isActive
      ? 'bg-brand-primary text-brand-dark-deepest font-semibold border-2 border-brand-primary'
      : 'bg-brand-dark-lighter text-brand-gray border border-brand-dark-lighter hover:bg-[#3A3D44] hover:text-brand-primary'
    }`;

  const monthBtnClass = (isActive: boolean, isDisabled: boolean) =>
    `px-2.5 py-1 rounded-md text-[11px] font-medium transition-all duration-150
     focus:outline-none
     ${isDisabled
      ? 'text-brand-gray/30 cursor-not-allowed'
      : isActive
        ? 'bg-brand-primary text-brand-dark-deepest font-semibold'
        : 'bg-brand-dark-lighter text-brand-gray border border-brand-dark-lighter hover:bg-[#3A3D44] hover:text-brand-primary cursor-pointer'
    }`;

  // For current month of current year, cap endDate at today (not end of month)
  // so projections aren't distorted by including future days
  const cappedEndDate = useCallback((year: number, monthIndex: number): string => {
    if (year === currentYear && monthIndex === currentMonth) {
      return fmt(today);
    }
    return fmt(lastDayOfMonth(year, monthIndex));
  }, [currentYear, currentMonth, today]);

  // Handlers
  const selectYTD = useCallback(() => {
    setMonthlyOpen(false);
    setDailyOpen(false);
    onPeriodChange({
      startDate: fmt(new Date(currentYear, 0, 1)),
      endDate: fmt(today),
    });
  }, [currentYear, today, onPeriodChange]);

  const selectYear = useCallback((year: number) => {
    setMonthlyOpen(false);
    setDailyOpen(false);
    onPeriodChange({
      startDate: fmt(new Date(year, 0, 1)),
      endDate: fmt(new Date(year, 11, 31)),
    });
  }, [onPeriodChange]);

  const toggleMonthly = useCallback(() => {
    if (monthlyOpen) {
      // Closing monthly → revert to the year's full selection
      setMonthlyOpen(false);
    } else {
      // Opening monthly → set year context based on current selection
      const year = active.type === 'ytd' ? currentYear : (active.type === 'year' || active.type === 'months') ? active.year : currentYear;
      setMonthlyYear(year);
      setMonthlyOpen(true);
      setDailyOpen(false);
    }
  }, [monthlyOpen, active, currentYear]);

  const toggleDaily = useCallback(() => {
    if (dailyOpen) {
      setDailyOpen(false);
    } else {
      setDailyOpen(true);
      setMonthlyOpen(false);
    }
  }, [dailyOpen]);

  const selectDay = useCallback((isoDate: string) => {
    if (!isoDate) return;
    onPeriodChange({ startDate: isoDate, endDate: isoDate });
  }, [onPeriodChange]);

  const selectMonth = useCallback((monthIndex: number) => {
    const year = monthlyYear;

    // If no months selected yet, or clicking outside current range → start new single month
    if (active.type !== 'months' || active.year !== year) {
      onPeriodChange({
        startDate: fmt(new Date(year, monthIndex, 1)),
        endDate: cappedEndDate(year, monthIndex),
      });
      return;
    }

    // Extend or set range
    const { monthStart, monthEnd } = active;
    if (monthStart === undefined || monthEnd === undefined) {
      onPeriodChange({
        startDate: fmt(new Date(year, monthIndex, 1)),
        endDate: cappedEndDate(year, monthIndex),
      });
      return;
    }

    // If clicking the same single month → deselect (go back to full year, keep monthly open)
    if (monthStart === monthEnd && monthStart === monthIndex) {
      if (year === currentYear) {
        onPeriodChange({
          startDate: fmt(new Date(currentYear, 0, 1)),
          endDate: fmt(today),
        });
      } else {
        onPeriodChange({
          startDate: fmt(new Date(year, 0, 1)),
          endDate: fmt(new Date(year, 11, 31)),
        });
      }
      return;
    }

    // If clicking a month that's already in the range → shrink the range
    if (monthIndex >= monthStart && monthIndex <= monthEnd) {
      // Clicking at the start → shrink from left
      if (monthIndex === monthStart) {
        onPeriodChange({
          startDate: fmt(new Date(year, monthStart + 1, 1)),
          endDate: cappedEndDate(year, monthEnd),
        });
      // Clicking at the end → shrink from right
      } else if (monthIndex === monthEnd) {
        onPeriodChange({
          startDate: fmt(new Date(year, monthStart, 1)),
          endDate: cappedEndDate(year, monthEnd - 1),
        });
      // Clicking in the middle → shrink to the left portion (start to clicked-1)
      } else {
        onPeriodChange({
          startDate: fmt(new Date(year, monthStart, 1)),
          endDate: cappedEndDate(year, monthIndex - 1),
        });
      }
      return;
    }

    // Create contiguous range from first selected to clicked month
    const newStart = Math.min(monthStart, monthIndex);
    const newEnd = Math.max(monthEnd, monthIndex);
    onPeriodChange({
      startDate: fmt(new Date(year, newStart, 1)),
      endDate: cappedEndDate(year, newEnd),
    });
  }, [monthlyYear, active, onPeriodChange, cappedEndDate, selectYTD, selectYear]);

  // Determine how many months to show for the selected year
  const maxMonth = monthlyYear === currentYear ? currentMonth : 11;

  // Determine if "Mensal" is the active mode
  const isMonthlyActive = monthlyOpen || active.type === 'months';

  // Determine if "Diário" is the active mode
  const isDailyActive = dailyOpen || active.type === 'day';

  // Today ISO for the date input default / max
  const todayIso = fmt(today);
  const currentDay = active.type === 'day' && active.day ? active.day : todayIso;

  // Legacy dropdown support (other pages)
  if (variant === 'dropdown') {
    const currentPreset = detectPreset(startDate, endDate);
    return (
      <select
        value={currentPreset}
        onChange={(e) => {
          const preset = e.target.value as PeriodPreset;
          if (preset !== 'custom') onPeriodChange(calculatePeriodDates(preset));
        }}
        className="px-3 py-1.5 rounded-lg border border-brand-dark-lighter bg-brand-dark-lighter text-white text-xs
                   cursor-pointer min-w-[140px] focus:outline-none focus:ring-2 focus:ring-brand-primary"
      >
        {(['ytd', 'prevYear', 'last12m', 'last6m', 'last3m'] as PeriodPreset[]).map((preset) => (
          <option key={preset} value={preset}>
            {preset === 'ytd' ? 'Ano Atual' : preset === 'prevYear' ? 'Ano Anterior' : preset === 'last12m' ? 'Últimos 12 meses' : preset === 'last6m' ? 'Últimos 6 meses' : 'Últimos 3 meses'}
          </option>
        ))}
        {currentPreset === 'custom' && <option value="custom">Personalizado</option>}
      </select>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {/* Year buttons + Mensal toggle */}
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => {
            if (monthlyOpen) {
              setMonthlyYear(currentYear);
              // Also update the data to this year
              onPeriodChange({
                startDate: fmt(new Date(currentYear, 0, 1)),
                endDate: fmt(today),
              });
            } else {
              selectYTD();
            }
          }}
          className={btnClass(
            monthlyOpen
              ? monthlyYear === currentYear
              : (active.type === 'ytd' || (active.type === 'months' && active.year === currentYear))
          )}
        >
          Ano Atual
        </button>

        {pastYears.map((year) => (
          <button
            key={year}
            onClick={() => {
              if (monthlyOpen) {
                setMonthlyYear(year);
                // Also update the data to this year
                onPeriodChange({
                  startDate: fmt(new Date(year, 0, 1)),
                  endDate: fmt(new Date(year, 11, 31)),
                });
              } else {
                selectYear(year);
              }
            }}
            className={btnClass(
              monthlyOpen
                ? monthlyYear === year
                : (active.type === 'year' && active.year === year) || (active.type === 'months' && active.year === year)
            )}
          >
            {year}
          </button>
        ))}

        <button
          onClick={toggleMonthly}
          className={btnClass(isMonthlyActive)}
        >
          Mensal {monthlyOpen ? '▴' : '▾'}
        </button>

        <button
          onClick={toggleDaily}
          className={btnClass(isDailyActive)}
        >
          Diário {dailyOpen ? '▴' : '▾'}
        </button>
      </div>

      {/* Month pills row */}
      {monthlyOpen && (
        <div className="flex gap-1.5 flex-wrap pl-0.5">
          {MONTH_LABELS.map((label, idx) => {
            const isDisabled = idx > maxMonth;
            const isInRange =
              active.type === 'months' &&
              active.year === monthlyYear &&
              active.monthStart !== undefined &&
              active.monthEnd !== undefined &&
              idx >= active.monthStart &&
              idx <= active.monthEnd;

            return (
              <button
                key={label}
                disabled={isDisabled}
                onClick={() => !isDisabled && selectMonth(idx)}
                className={monthBtnClass(isInRange, isDisabled)}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      {/* Daily date input row */}
      {dailyOpen && (
        <div className="flex items-center gap-2 pl-0.5">
          <label className="text-[11px] text-brand-gray">Selecione o dia:</label>
          <input
            type="date"
            value={currentDay}
            max={todayIso}
            onChange={(e) => selectDay(e.target.value)}
            className="px-2.5 py-1 rounded-md text-[11px] font-medium bg-brand-dark-lighter text-brand-gray border border-brand-dark-lighter
                       focus:outline-none focus:ring-2 focus:ring-brand-primary focus:text-white cursor-pointer
                       [color-scheme:dark]"
          />
        </div>
      )}
    </div>
  );
}

export default PeriodPresetSelector;
